/**
 * App Component
 *
 * Root application component with:
 * - Material-UI theme provider
 * - React Router setup
 * - Auth store initialization
 * - Main routing configuration
 * - Lazy loading for routes (code splitting)
 */

import { useEffect, lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { ThemeProvider } from '@mui/material/styles'
import { CssBaseline, Box, CircularProgress } from '@mui/material'
import { initializeAuth } from '@/stores/authStore'
import { theme } from '@/theme/theme'
import { ErrorBoundaryWrapper } from '@/components/common/ErrorBoundary'
import { ProtectedRoute } from '@/components/common/ProtectedRoute'
import { ProfileCheckRoute } from '@/components/common/ProfileCheckRoute'
import { OfflineIndicator } from '@/components/common/OfflineIndicator'
import { InstallPrompt } from '@/components/common/InstallPrompt'

// Lazy load pages for code splitting
const HomePage = lazy(() => import('@/pages/HomePage'))
const ConversationsListPage = lazy(() => import('@/pages/ConversationsListPage'))
const MessageThreadPage = lazy(() => import('@/pages/MessageThreadPage'))
const UserProfilePage = lazy(() => import('@/pages/UserProfilePage'))
const ProfileSetupPage = lazy(() => import('@/pages/ProfileSetupPage'))
const PrivacyPolicyPage = lazy(() => import('@/pages/PrivacyPolicyPage'))
const TermsOfServicePage = lazy(() => import('@/pages/TermsOfServicePage'))

// Loading fallback component
function LoadingFallback() {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
      }}
      role="status"
      aria-label="Φόρτωση σελίδας"
    >
      <CircularProgress />
    </Box>
  )
}

function App() {
  // Initialize auth store on app mount
  useEffect(() => {
    initializeAuth()
  }, [])

  return (
    <ErrorBoundaryWrapper>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <OfflineIndicator />
        <InstallPrompt />
        <BrowserRouter>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<HomePage />} />

              {/* Legal pages */}
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsOfServicePage />} />

              {/* Protected routes */}
              <Route
                path="/profile-setup"
                element={
                  <ProtectedRoute redirectTo="/">
                    <ProfileSetupPage />
                  </ProtectedRoute>
                }
              />

              {/* Redirect /dashboard to / (Facebook-style feed now on homepage) */}
              <Route path="/dashboard" element={<Navigate to="/" replace />} />

              {/* Messaging routes */}
              <Route
                path="/messages"
                element={
                  <ProtectedRoute redirectTo="/">
                    <ProfileCheckRoute>
                      <ConversationsListPage />
                    </ProfileCheckRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/messages/:conversationId"
                element={
                  <ProtectedRoute redirectTo="/">
                    <ProfileCheckRoute>
                      <MessageThreadPage />
                    </ProfileCheckRoute>
                  </ProtectedRoute>
                }
              />

              {/* User profile route */}
              <Route
                path="/profile/:userId"
                element={
                  <ProtectedRoute redirectTo="/">
                    <ProfileCheckRoute>
                      <UserProfilePage />
                    </ProfileCheckRoute>
                  </ProtectedRoute>
                }
              />

              {/* Catch-all redirect */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundaryWrapper>
  )
}

export default App
